// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const USE_CASES = [
  { id: 'menu', label: 'Restaurant Menu', icon: '\uD83C\uDF7D\uFE0F' },
  { id: 'card', label: 'Business Card', icon: '\uD83D\uDCBC' },
  { id: 'sign', label: 'Storefront Sign', icon: '\uD83C\uDFEA' },
  { id: 'product', label: 'Product Packaging', icon: '\uD83D\uDCE6' },
  { id: 'event', label: 'Event Check-in', icon: '\uD83C\uDF9F\uFE0F' },
  { id: 'wifi', label: 'WiFi Access', icon: '\uD83D\uDCF6' },
];

const DEMO_CODES = [
  { id: 1, label: 'Main Website', url: 'https://mybusiness.pages.dev', scans: 342, created: 'Feb 10, 2026', type: 'url' },
  { id: 2, label: 'Digital Menu', url: 'https://mybusiness.pages.dev/menu', scans: 1240, created: 'Feb 5, 2026', type: 'menu' },
  { id: 3, label: 'Contact Card', url: 'vcard:John Smith,CEO,mybusiness.ca', scans: 89, created: 'Jan 28, 2026', type: 'card' },
];

function QRCodePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('generator');
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [size, setSize] = useState('256');
  const [cornerStyle, setCornerStyle] = useState('square');

  const renderGenerator = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
      <div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Create QR Code</h3>
          <div style={{ marginBottom: 12 }}><label style={labelStyle}>Label</label><input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="My QR Code" style={inputStyle} /></div>
          <div style={{ marginBottom: 12 }}><label style={labelStyle}>URL or Content</label><input type="text" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://mybusiness.pages.dev" style={inputStyle} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={labelStyle}>QR Color</label><input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width: '100%', height: 40, border: 'none', borderRadius: 8, cursor: 'pointer' }} /></div>
            <div><label style={labelStyle}>Background</label><input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} style={{ width: '100%', height: 40, border: 'none', borderRadius: 8, cursor: 'pointer' }} /></div>
            <div><label style={labelStyle}>Size (px)</label><select value={size} onChange={e => setSize(e.target.value)} style={inputStyle}><option>128</option><option>256</option><option>512</option><option>1024</option></select></div>
            <div><label style={labelStyle}>Corners</label><select value={cornerStyle} onChange={e => setCornerStyle(e.target.value)} style={inputStyle}><option value="square">Square</option><option value="rounded">Rounded</option></select></div>
          </div>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Quick Templates</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {USE_CASES.map(u => (
              <div key={u.id} onClick={() => setLabel(u.label)} style={{ padding: 12, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>{u.icon}</div>
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, textAlign: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Preview</h3>
          <div style={{ width: 200, height: 200, margin: '0 auto 16px', background: bgColor, borderRadius: cornerStyle === 'rounded' ? 16 : 4, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)' }}>
            <div style={{ width: 160, height: 160, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'repeat(7, 1fr)', gap: 2 }}>
              {Array.from({ length: 49 }, (_, i) => {
                const isCorner = (i < 3 || (i > 3 && i < 7)) && (Math.floor(i / 7) < 3) || (i % 7 >= 4 && Math.floor(i / 7) < 3) || (i % 7 < 3 && Math.floor(i / 7) >= 4);
                return <div key={i} style={{ background: Math.random() > 0.4 || isCorner ? color : 'transparent', borderRadius: cornerStyle === 'rounded' ? 2 : 0 }} />;
              })}
            </div>
          </div>
          {label && <p style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, marginBottom: 4 }}>{label}</p>}
          {url && <p style={{ fontSize: 11, color: 'var(--text-muted)', wordBreak: 'break-all' }}>{url}</p>}
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          <button style={{ padding: '10px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Download PNG</button>
          <button style={{ padding: '10px 16px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>Download SVG</button>
          <button style={{ padding: '10px 16px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>Download PDF</button>
        </div>
      </div>
    </div>
  );

  const renderHistory = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>My QR Codes</h2>
      <div style={{ display: 'grid', gap: 10 }}>
        {DEMO_CODES.map(c => (
          <div key={c.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 60, height: 60, background: 'var(--bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--text-muted)', border: '1px solid var(--border)' }}>QR</div>
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{c.label}</h4>
              <p style={{ fontSize: 12, color: '#6366f1', wordBreak: 'break-all' }}>{c.url}</p>
            </div>
            <div style={{ textAlign: 'center', minWidth: 60 }}>
              <p style={{ fontSize: 18, fontWeight: 700, color: '#6366f1' }}>{c.scans}</p>
              <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>scans</p>
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.created}</span>
            <button style={{ padding: '6px 12px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Download</button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderNFC = () => (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 24 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>NFC Tag Programming</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>Program NFC tags to link customers directly to your website, menu, or contact card.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>How to Program</h3>
          <ol style={{ paddingLeft: 20, color: 'var(--text-muted)', fontSize: 14, lineHeight: 2 }}>
            <li>Purchase NFC tags (NTAG215 or NTAG216 recommended)</li>
            <li>Download an NFC writer app on your phone</li>
            <li>Copy the URL from your QR code above</li>
            <li>Open the NFC writer app and paste the URL</li>
            <li>Hold the NFC tag against your phone to write</li>
            <li>Test by tapping the tag with any NFC-enabled phone</li>
          </ol>
        </div>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Recommended Tags</h3>
          {[{ name: 'NTAG215 Stickers (50 pack)', price: '$24.99', desc: 'Adhesive stickers, 540 bytes' }, { name: 'NTAG216 Cards (25 pack)', price: '$32.99', desc: 'Business card size, 888 bytes' }, { name: 'Epoxy NFC Tags (10 pack)', price: '$18.99', desc: 'Waterproof, for outdoor use' }].map((t, i) => (
            <div key={i} style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 8 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{t.name}</h4>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.desc}</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#6366f1', marginTop: 4 }}>{t.price}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{'\uD83D\uDD17'} QR Codes & NFC</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 15 }}>Generate QR codes and program NFC tags for your business.</p>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['generator', 'history', 'nfc'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 20px', background: tab === t ? '#6366f1' : 'var(--card)', color: tab === t ? '#fff' : 'var(--text-muted)', border: tab === t ? 'none' : '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>{t === 'nfc' ? 'NFC Tags' : t}</button>
        ))}
      </div>
      {tab === 'generator' && renderGenerator()}
      {tab === 'history' && renderHistory()}
      {tab === 'nfc' && renderNFC()}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default QRCodePage;
